import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api.js";

type MarketplaceLeadStatus = "NEW" | "CONTACTED" | "WON" | "LOST" | "CANCELLED";

interface MarketplaceLead {
  id: string;
  status: MarketplaceLeadStatus;
  createdAt: string;
  updatedAt: string;
}

interface MarketplaceOffer {
  id: string;
  category: string;
  title: string;
  description: string;
  ctaLabel: string;
  externalUrl: string | null;
  advertiserName: string | null;
  erid: string | null;
  partner: { id: string; name: string };
  existingLead: MarketplaceLead | null;
}

interface MarketplacePayload {
  consent: {
    version: string;
    text: string;
  };
  offers: MarketplaceOffer[];
}

const CATEGORY_LABELS: Record<string, string> = {
  MATERIALS: "Материалы",
  EDUCATION: "Обучение",
  FINANCE: "Финансы",
  SERVICES: "Сервисы",
  RENT: "Аренда",
  OTHER: "Другое",
};

const LEAD_STATUS_LABELS: Record<MarketplaceLeadStatus, string> = {
  NEW: "Заявка отправлена",
  CONTACTED: "Партнер на связи",
  WON: "Подключено",
  LOST: "Не подошло",
  CANCELLED: "Отменено",
};

export function MarketplacePage({ token }: { token: string }) {
  const queryClient = useQueryClient();
  const [consentByOfferId, setConsentByOfferId] = useState<Record<string, boolean>>({});
  const [messageByOfferId, setMessageByOfferId] = useState<Record<string, string>>({});
  const [formErrorByOfferId, setFormErrorByOfferId] = useState<Record<string, string>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ["master-marketplace-offers"],
    queryFn: () => apiFetch<MarketplacePayload>(token, "/api/business/me/marketplace/offers"),
  });

  const requestMutation = useMutation({
    mutationFn: (input: { offerId: string; message: string }) =>
      apiFetch<{ created: boolean; lead: MarketplaceLead }>(
        token,
        `/api/business/me/marketplace/offers/${input.offerId}/request`,
        {
          method: "POST",
          body: JSON.stringify({
            consentAccepted: true,
            message: input.message.trim() || undefined,
          }),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["master-marketplace-offers"] });
    },
  });

  const clickMutation = useMutation({
    mutationFn: (offer: MarketplaceOffer) =>
      apiFetch<{ tracked: boolean; externalUrl: string | null }>(
        token,
        `/api/business/me/marketplace/offers/${offer.id}/click`,
        { method: "POST", body: JSON.stringify({}) },
      ),
    onSuccess: (result) => {
      if (result.externalUrl) {
        window.open(result.externalUrl, "_blank", "noopener,noreferrer");
      }
    },
  });

  function submitRequest(offer: MarketplaceOffer) {
    if (!consentByOfferId[offer.id]) {
      setFormErrorByOfferId((prev) => ({
        ...prev,
        [offer.id]: "Сначала подтвердите согласие на передачу контакта.",
      }));
      return;
    }

    setFormErrorByOfferId((prev) => ({ ...prev, [offer.id]: "" }));
    requestMutation.mutate({
      offerId: offer.id,
      message: messageByOfferId[offer.id] ?? "",
    });
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4 pb-24">
      <header className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Выгодно</p>
        <h1 className="mt-1 text-2xl font-bold text-gray-950">Партнерские предложения</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
          Контакт передается партнеру только после отправки заявки.
        </p>
      </header>

      {isLoading && (
        <div className="rounded-xl border border-gray-100 bg-white p-4 text-sm text-gray-500 shadow-sm">
          Загружаем предложения...
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-medium text-red-700">
          Не удалось загрузить предложения.
        </div>
      )}

      {!isLoading && data?.offers.length === 0 && (
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="text-base font-bold text-gray-900">Пока нет активных предложений</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">
            Новые партнерские офферы появятся здесь после отбора оператором платформы.
          </p>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {data?.offers.map((offer) => {
          const lead = offer.existingLead;
          const hasOpenLead = lead?.status === "NEW" || lead?.status === "CONTACTED";
          const formError = formErrorByOfferId[offer.id];
          const isRequesting = requestMutation.isPending && requestMutation.variables?.offerId === offer.id;
          const isClicking = clickMutation.isPending && clickMutation.variables?.id === offer.id;

          return (
            <article key={offer.id} className="flex min-w-0 flex-col rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">
                    {CATEGORY_LABELS[offer.category] ?? offer.category}
                  </p>
                  <h2 className="mt-1 text-lg font-bold leading-6 text-gray-950">{offer.title}</h2>
                  <p className="mt-1 text-sm font-medium text-gray-500">{offer.partner.name}</p>
                </div>
                {lead && (
                  <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                    {LEAD_STATUS_LABELS[lead.status]}
                  </span>
                )}
              </div>

              <p className="mt-3 whitespace-pre-line text-sm leading-6 text-gray-700">{offer.description}</p>

              {(offer.advertiserName || offer.erid) && (
                <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs leading-5 text-gray-500">
                  {offer.advertiserName ? `Рекламодатель: ${offer.advertiserName}` : "Партнерское предложение"}
                  {offer.erid ? ` · ERID: ${offer.erid}` : ""}
                </p>
              )}

              <div className="mt-4 space-y-3">
                <textarea
                  value={messageByOfferId[offer.id] ?? ""}
                  onChange={(event) =>
                    setMessageByOfferId((prev) => ({ ...prev, [offer.id]: event.target.value }))
                  }
                  maxLength={500}
                  rows={3}
                  disabled={hasOpenLead}
                  placeholder="Комментарий для партнера"
                  className="w-full resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:bg-gray-50"
                />

                <label className="flex items-start gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-sm leading-5 text-gray-700">
                  <input
                    type="checkbox"
                    checked={Boolean(consentByOfferId[offer.id])}
                    disabled={hasOpenLead}
                    onChange={(event) =>
                      setConsentByOfferId((prev) => ({ ...prev, [offer.id]: event.target.checked }))
                    }
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span>{data.consent.text}</span>
                </label>

                {formError && <p className="text-sm font-medium text-red-600">{formError}</p>}

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={hasOpenLead || isRequesting}
                    onClick={() => submitRequest(offer)}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {hasOpenLead ? "Заявка уже отправлена" : isRequesting ? "Отправляем..." : offer.ctaLabel}
                  </button>
                  {offer.externalUrl && (
                    <button
                      type="button"
                      disabled={isClicking}
                      onClick={() => clickMutation.mutate(offer)}
                      className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
                    >
                      {isClicking ? "Открываем..." : "Перейти"}
                    </button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
